import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { Alert, AlertService } from '../../shared/services/alert.service';
import { ReflectionService } from '../../shared/utilities/reflection.service';
import { ActionButton, ActionButtonClickEvent, ActionComplete, ColumnDefinition } from '../table.types';

@Component({
    selector: 'app-table-action',
    templateUrl: './table-action.component.html',
    styleUrls: ['./table-action.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class TableActionComponent implements OnInit {
    @Input() action: ActionButton;
    @Input() element: any;

    show: boolean;
    toolTip: string;
    color: ThemePalette;
    takingAction: boolean;

    constructor(private reflection: ReflectionService, private alert: AlertService) {}

    ngOnInit(): void {
        this.show = this.showAction(this.action, this.element);
        this.toolTip = this.computeTooltip(this.action, this.element);
        this.color = this.computeColor(this.action);
    }

    showAction(action: ActionButton, element: any): boolean {
        if (action.disabled === true) {
            return false;
        }
        if (action.showCallback && !action.showCallback.function.call(action.showCallback.this, element)) {
            return false;
        }
        return true;
    }

    computeTooltip(action: ActionButton, element): string {
        if (action.toolTip) {
            return this.reflection.stringDataMerge(element, action.toolTip);
        } else if (action.discriminator === 'edit') {
            return 'Edit';
        } else if (action.discriminator === 'delete') {
            return 'Delete';
        }
    }

    computeColor(action: ActionButton): ThemePalette {
        if (action.color) {
            return action.color;
        } else if (action.discriminator === 'edit') {
            return 'primary';
        } else if (action.discriminator === 'delete') {
            return 'warn';
        }
    }

    onClick() {
        this.takingAction = true;
        const doConfirm: boolean =
            (this.action.discriminator === 'delete' && !this.action.skipDeleteConfirm) || this.action.requireConfirm;
        if (doConfirm) {
            const text = this.reflection.stringDataMerge(this.element, this.action.confirmMessage);
            this.alert.confirm({
                title: this.reflection.getValueOrDefault(this.action.confirmTitle, 'Are you sure?'),
                okButtonText: this.reflection.getValueOrDefault(this.action.okButtonText, 'Confirm'),
                cancelButtonText: this.reflection.getValueOrDefault(this.action.cancelButtonText, 'Cancel'),
                text: text,
                callBack: {
                    function: (result: boolean) => {
                        if (result === true) {
                            this.doAction();
                        } else {
                            this.completeAction();
                        }
                    },
                    this: this
                }
            });
        } else {
            this.doAction();
        }
    }

    doAction() {
        if (this.action.clickCallBack.observable) {
            this.action.clickCallBack.observable
                .call(this.action.clickCallBack.this, {
                    element: this.element,
                    action: this.action
                } as ActionButtonClickEvent<any>)
                .subscribe({
                    next: (next) => {
                        const complete: ActionComplete = this.action.clickCallBack.function.call(
                            this.action.clickCallBack.this,
                            {
                                element: this.element,
                                observableValue: next,
                                action: this.action
                            } as ActionButtonClickEvent<any>
                        );
                        this.completeAction(complete);
                    },
                    error: (error) => {
                        const message = this.action.clickCallBack.errorMessage
                            ? this.action.clickCallBack.errorMessage
                            : this.reflection.objectToString(error?.error);
                        this.alert.toastError(message);
                        console.error(error);
                        this.completeAction();
                    }
                });
        } else {
            const complete: ActionComplete = this.action.clickCallBack.function.call(this.action.clickCallBack.this, {
                element: this.element,
                action: this.action
            } as ActionButtonClickEvent<any>);
            this.completeAction(complete);
        }
    }

    completeAction(complete?: ActionComplete) {
        this.takingAction = false;
    }
}
